import { PreviewAnomaly } from '@api/twin/AnomalyApi';
import { EyeIcon } from '@heroicons/react/24/outline';
import { Icon } from '@stereograph/teia-system-design/components/Icon';
import { IconButton } from '@stereograph/teia-system-design/components/IconButton';
import { CircularProgress } from '@stereograph/teia-system-design/components/Progress';
import { Tooltip } from '@stereograph/teia-system-design/components/Tooltip';
import {
  useTeiaViewerContext,
} from '@stereograph/teiaviewer';
import { usePluginTranslation } from '@translation';
import { useCallback, useState } from 'react';
import { SetAnomalySceneCommand } from '../../command/SetAnomalySceneCommand';
import { useEnsureAnomaly, useHasCachedAnomaly, usePrefetchAnomaly } from '../../hooks/useAnomalyQueryOptions';
import { createAnomalyScene } from '../../widgets/utils';

interface VisualizeSelectionButtonProps {
  previewAnomaly: PreviewAnomaly;
}

export const VisualizeSelectionButton = (props: VisualizeSelectionButtonProps) => {
  const { previewAnomaly } = props;
  const { t } = usePluginTranslation();
  const { viewer } = useTeiaViewerContext();
  const prefetch = usePrefetchAnomaly(previewAnomaly.projectId, previewAnomaly.id);
  const hasCachedData = useHasCachedAnomaly(previewAnomaly.projectId, previewAnomaly.id);
  const ensureAnomaly = useEnsureAnomaly(previewAnomaly.projectId, previewAnomaly.id);

  const [isBusy, setIsBusy] = useState(false);

  const onClick = useCallback(async () => {
    try {
      if (!hasCachedData) {
        setIsBusy(true);
      }
      const ensureAnomalyData = await ensureAnomaly();
      if (!ensureAnomalyData.stateByGuid) {
        return;
      }

      const scene = createAnomalyScene(ensureAnomalyData.stateByGuid);
      new SetAnomalySceneCommand(viewer, scene).execute();
    } finally {
      setIsBusy(false);
    }
  }, [ensureAnomaly, hasCachedData, viewer]);

  return (
    <Tooltip
      title={t('anomaly_plugin.resume_widget.table.columns.actions.visualize_selection_tooltip')}
      placement="top"
    >
      <IconButton
        aria-label={t(
          'anomaly_plugin.resume_widget.table.columns.actions.visualize_selection_tooltip',
        )}
        onClick={onClick}
        onPointerEnter={prefetch}
        iconOnly
        data-dd-action-name="tvp.anomaly.visualize_selection"
      >
        {isBusy
          ? <CircularProgress size={20} />
          : (
            <Icon
              component={EyeIcon}
              sx={{
                color: (theme) => theme.palette.primary.main,
              }}
            />
          )}
      </IconButton>
    </Tooltip>
  );
};
